/**
 * Engine aggro kit — cumulative threat scores per attacker → targets.
 * Domain injects amounts; no catalog imports.
 */

import { pickSticky, type StickyPickKnobs } from "./target-pick";

export type AggroModifier = {
  targetId: number;
  /** Multiplier applied to stored threat when ranking (e.g. taunt). */
  mul: number;
  /** Absolute sim time; modifier expires when time >= until. */
  until: number;
};

export type AggroTable = {
  /** targetId → raw threat */
  scores: Map<number, number>;
  modifiers: AggroModifier[];
};

export function createAggroTable(): AggroTable {
  return { scores: new Map(), modifiers: [] };
}

export function addThreat(
  table: AggroTable,
  targetId: number,
  amount: number,
): void {
  if (!(amount > 0) || !Number.isFinite(amount)) return;
  const prev = table.scores.get(targetId) ?? 0;
  table.scores.set(targetId, prev + amount);
}

/** Linear decay toward zero; removes near-zero entries. */
export function decayThreat(
  table: AggroTable,
  dt: number,
  ratePerSec: number,
): void {
  if (!(dt > 0) || !(ratePerSec > 0)) return;
  const factor = Math.max(0, 1 - ratePerSec * dt);
  for (const [id, v] of table.scores) {
    const next = v * factor;
    if (next < 0.05) table.scores.delete(id);
    else table.scores.set(id, next);
  }
}

export function addAggroModifier(
  table: AggroTable,
  targetId: number,
  mul: number,
  until: number,
): void {
  if (!(mul > 0)) return;
  table.modifiers.push({ targetId, mul, until });
}

export function pruneAggroModifiers(table: AggroTable, time: number): void {
  if (table.modifiers.length === 0) return;
  table.modifiers = table.modifiers.filter((m) => m.until > time);
}

export function effectiveThreat(
  table: AggroTable,
  targetId: number,
  time: number,
): number {
  const base = table.scores.get(targetId) ?? 0;
  let mul = 1;
  for (const m of table.modifiers) {
    if (m.targetId === targetId && m.until > time) mul *= m.mul;
  }
  return base * mul;
}

export type AggroTopOpts<T> = {
  idOf: (t: T) => number;
  /** Extra domain score (e.g. in-range bonus) added to effective threat. */
  bonusOf?: (t: T) => number;
  /**
   * Optional transform of stored effective threat (e.g. softcap boss for engage).
   * Applied before bonusOf.
   */
  threatOf?: (targetId: number, effective: number) => number;
  currentId: number;
  sticky: StickyPickKnobs;
  time: number;
};

/**
 * Pick highest effective-threat candidate with sticky damping.
 * Candidates with non-positive effective+bonus are skipped unless all are ≤0
 * (then sticky still picks the least-bad / first sticky current).
 */
export function topThreatTarget<T>(
  table: AggroTable,
  candidates: readonly T[],
  opts: AggroTopOpts<T>,
): T | null {
  pruneAggroModifiers(table, opts.time);
  return pickSticky(
    candidates,
    opts.currentId,
    opts.idOf,
    (t) => {
      const id = opts.idOf(t);
      const raw = effectiveThreat(table, id, opts.time);
      const threat = opts.threatOf?.(id, raw) ?? raw;
      const bonus = opts.bonusOf?.(t) ?? 0;
      return threat + bonus;
    },
    opts.sticky,
  );
}

/** Descending ranks for HUD / debug. */
export function threatRanks(
  table: AggroTable,
  candidateIds: readonly number[],
  time: number,
  limit = 5,
): { targetId: number; threat: number; rank: number }[] {
  pruneAggroModifiers(table, time);
  const rows = candidateIds.map((targetId) => ({
    targetId,
    threat: effectiveThreat(table, targetId, time),
  }));
  rows.sort((a, b) => b.threat - a.threat);
  return rows.slice(0, Math.max(0, limit)).map((r, i) => ({
    ...r,
    rank: i + 1,
  }));
}
